import clsx from 'clsx';
import type { ButtonHTMLAttributes, FunctionComponent, MouseEventHandler, ReactNode } from 'react';

import { Spinner } from '../../core/spinner/Spinner';
import { floatingSurfaceClassNames, floatingSurfaceItemClassNames } from '../../core/surface/surfaceClasses';

export const GlobalSearchDropdownContainer: FunctionComponent<{
    readonly children: ReactNode;
    readonly className?: string;
}> = ({ children, className }) => (
    <div
        className={clsx(
            floatingSurfaceClassNames.tone.white,
            'font-sans rounded-lg p-xs overflow-y-auto max-h-150 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
            className,
        )}
        role="listbox"
    >
        {children}
    </div>
);

export const GlobalSearchDropdownLabel: FunctionComponent<{ readonly children: ReactNode }> = ({ children }) => (
    <div className="text-gray-700 text-xxs font-normal p-s select-none">{children}</div>
);

export const GlobalSearchDropdownSeparator: FunctionComponent = () => <hr className="bg-gray-300 h-px m-xs border-0" />;

type GlobalSearchDropdownItemProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
    readonly children: ReactNode;
    readonly isSelected?: boolean;
    readonly onClick: MouseEventHandler<HTMLButtonElement>;
};

export const GlobalSearchDropdownItem: FunctionComponent<GlobalSearchDropdownItemProps> = ({
    children,
    className,
    isSelected = false,
    onClick,
    ...props
}) => (
    <button
        className={clsx(
            'flex items-center w-full bg-transparent border-0 appearance-none',
            'rounded-sm text-gray-900 cursor-pointer font-sans',
            'text-xxs font-normal leading-[1rem]',
            'py-s px-m relative select-none text-left',
            'transition-colors duration-200 ease',
            'focus:bg-gray-200 focus-visible:outline-none',
            floatingSurfaceItemClassNames.hover,
            isSelected === true && '!bg-gray-300 hover:!bg-gray-300',
            'overflow-hidden text-ellipsis whitespace-nowrap',
            className,
        )}
        role="option"
        type="button"
        onClick={onClick}
        {...props}
    >
        {children}
    </button>
);

export const GlobalSearchDropdownSeeMore: FunctionComponent<{
    readonly children: ReactNode;
    readonly onClick: MouseEventHandler<HTMLButtonElement>;
}> = ({ children, onClick }) => (
    <button
        className={clsx(
            'flex items-center w-full bg-transparent border-0 appearance-none',
            'rounded-sm cursor-pointer font-sans',
            'text-xxs font-semibold leading-[1rem] text-gray-900',
            'py-s px-m text-left select-none',
            'transition-colors duration-200 ease',
            'focus-visible:outline-none focus:bg-gray-200',
            floatingSurfaceItemClassNames.hover,
        )}
        type="button"
        onClick={onClick}
    >
        {children}
    </button>
);

export const GlobalSearchDropdownLoading: FunctionComponent = () => (
    <div className="flex items-center justify-center p-m">
        <Spinner className="h-[1rem] w-[1rem] text-gray-700" />
    </div>
);

export const GlobalSearchDropdownEmpty: FunctionComponent<{ readonly children: ReactNode }> = ({ children }) => (
    <div className="flex items-center justify-center p-m text-gray-700 text-xxs font-normal leading-[1rem] select-none">
        {children}
    </div>
);
